/**
 * Who may change what once a survey is published (issue #176).
 *
 * Pure functions so the rules can be unit-tested without standing up MSAL or
 * the form builder. The role names are the AAD app roles this tenant issues —
 * a token for the creator app carries `roles: ["ct-user", "ct-admin"]` — and
 * `ct-admin` is the same value the backend treats as admin
 * (`LOCAL_DEV_ADMIN_ROLE` in `libs/auth`).
 */

/** AAD app role for the "Survey Creator Admin" described in issue #176. */
export const SURVEY_CREATOR_ADMIN_ROLE = "ct-admin";

/** True when the signed-in user holds the Survey Creator Admin role. */
export function isSurveyCreatorAdmin(roles: readonly string[] | undefined): boolean {
  return (roles ?? []).includes(SURVEY_CREATOR_ADMIN_ROLE);
}

/**
 * Question text (the question label) is the one field a Survey Creator Admin
 * may still change after publish. Everyone may edit it while the survey is
 * still a draft.
 */
export function canEditQuestionText(published: boolean, admin: boolean): boolean {
  return !published || admin;
}

/**
 * Everything other than question text — question types, options, skip logic,
 * attachments, adding/removing questions — is frozen at publish, for admins too.
 */
export function canEditSurveyStructure(published: boolean): boolean {
  return !published;
}

/**
 * Normalise the `roles` claim off an ID token into a plain string list.
 *
 * MSAL types this claim loosely, so an `Array.isArray` check alone would let a
 * non-string element through under a `string[]` annotation and make later
 * `includes` checks quietly meaningless. Kept here, beside the rules that
 * consume it, so it can be tested without standing up MSAL.
 */
export function toRoleList(claim: unknown): string[] {
  const raw = typeof claim === "string" ? [claim] : Array.isArray(claim) ? claim : [];
  // Trim both shapes identically and drop blanks. The earlier version tested
  // trim() but returned the untrimmed value, and skipped trimming for arrays, so
  // a claim carrying " ct-admin" would have failed an exact role match.
  return raw
    .filter((r): r is string => typeof r === "string")
    .map((r) => r.trim())
    .filter((r) => r !== "");
}
